import React from "react";
import styled from "styled-components";
import {useTranslation} from "react-i18next";
import {PureButton} from "components/Button";
import {useDispatch, useSelector} from "react-redux";
import {getActiveSubMenu} from "modules/selectors";
import {toggleSubMenu} from "modules/actions";
import {IDropDownLink, MENU_LINKS} from "modules";
import {chain} from "lodash";

const Wrapper = styled.nav`
	position: relative;
	display: flex;
	align-items: center;
	margin-inline-start: 16px;
	overflow-x: auto;
	-ms-overflow-style: none;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const SubMenuWrapper = styled.div<{bgc?: string}>`
	background-color: ${({bgc}) => bgc || "#2C4B86"};
	height: 56px;
	padding: 0 40px;
	position: absolute;
	left: 0;
	top: 100%;
	width: 100%;
	display: none;
	align-items: center;
	z-index: 1;

	&.open {
		display: flex;
	}
`;

const LinkWrapper = styled.a<{colour?: string}>`
	margin-inline-end: 16px;
	margin-inline-start: 16px;
	color: ${({colour}) => colour || "#fff"};
	font-weight: 500;
	font-size: 14px;
	white-space: nowrap;
	transition: 0.2s color ease-in-out;

	&:hover {
		color: #00b8ff;
	}
`;

const MenuLink: React.FC<IDropDownLink> = (link) => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const activeSubMenu = useSelector(getActiveSubMenu);

	const toggleMenu = () => {
		if (link.title === activeSubMenu) {
			dispatch(toggleSubMenu(null));
			return;
		}

		dispatch(toggleSubMenu(link.title));
	};

	if (link.subLinks) {
		return (
			<LinkWrapper as={PureButton} onClick={toggleMenu} colour={link.colour}>
				{t(link.title)}
			</LinkWrapper>
		);
	}

	return (
		<LinkWrapper href={link.href} colour={link.colour}>
			{t(link.title)}
		</LinkWrapper>
	);
};

export const MenuLinks: React.FC = () => (
	<Wrapper>
		{MENU_LINKS.map((link) => (
			<MenuLink key={link.title} {...link} />
		))}
	</Wrapper>
);

export const SubMenuLinks: React.FC = () => {
	const activeSubMenu = useSelector(getActiveSubMenu);
	const className = activeSubMenu ? "open" : "";
	const parentLink = chain(MENU_LINKS)
		.find((link) => link.title === activeSubMenu)
		.get("subLinks")
		.value();

	if (!parentLink) {
		return null;
	}

	return (
		<SubMenuWrapper className={className} bgc={parentLink.bgc}>
			{parentLink.links.map((link) => (
				<MenuLink key={link.title} {...link} colour={parentLink.colour} />
			))}
		</SubMenuWrapper>
	);
};
